import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Calendar, DollarSign, Users, TrendingUp } from 'lucide-react';
import StatsCard from '@/components/dashboard/StatsCard';
import RevenueChart from '@/components/dashboard/RevenueChart';
import QuickActions from '@/components/dashboard/QuickActions';
import TodayBookings from '@/components/dashboard/TodayBookings';
import { api } from '@/services/api';
import { Booking, DashboardStats } from '@/types';
import { formatNZD } from '@/utils/currency';
import { getNzDateString } from '@/utils/nzDate';

type RevenuePoint = {
  date: string;
  revenue: number;
  bookings: number;
};

const Dashboard: React.FC = () => {
  const { t, i18n } = useTranslation();

  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [, setStatsLoading] = useState(true);
  const [revenueData, setRevenueData] = useState<RevenuePoint[]>([]);
  const [revenueLoading, setRevenueLoading] = useState(true);
  const [todayBookings, setTodayBookings] = useState<Booking[]>([]);
  const [todayBookingsLoading, setTodayBookingsLoading] = useState(true);

  useEffect(() => {
    let isActive = true;

    const loadStats = async () => {
      setStatsLoading(true);
      const result = await api.analytics.getDashboardStats('7days');
      if (!isActive) return;
      if (result.error || !result.data) {
        setStats(null);
      } else {
        setStats(result.data);
      }
      setStatsLoading(false);
    };

    const loadRevenue = async () => {
      setRevenueLoading(true);
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 6);
      const result = await api.analytics.getRevenueReport({
        date_from: getNzDateString(start),
        date_to: getNzDateString(end),
        group_by: 'day'
      });
      if (!isActive) return;
      if (result.error || !Array.isArray(result.data)) {
        setRevenueData([]);
      } else {
        const mapped = result.data.map((row) => ({
          date: String(row.period ?? row.date ?? ''),
          revenue: Number(row.revenue ?? 0),
          bookings: Number(row.bookings ?? 0)
        }));
        setRevenueData(mapped);
      }
      setRevenueLoading(false);
    };

    const loadTodayBookings = async () => {
      setTodayBookingsLoading(true);
      const today = getNzDateString();
      const result = await api.bookings.getAll({ date: today, limit: 50 });
      if (!isActive) return;
      if (result.error || !result.data) {
        setTodayBookings([]);
      } else {
        setTodayBookings(result.data.bookings || []);
      }
      setTodayBookingsLoading(false);
    };

    loadStats();
    loadRevenue();
    loadTodayBookings();

    return () => {
      isActive = false;
    };
  }, []);

  const statsData = [
    {
      title: t('dashboard.todayBookings'),
      value: stats ? stats.today_bookings?.value ?? 0 : 0,
      change: stats
        ? {
            value: Number(stats.today_bookings?.change ?? 0),
            label: t('dashboard.comparedToYesterday'),
          }
        : undefined,
      icon: Calendar,
      color: 'primary' as const,
      trend: stats?.today_bookings?.trend ?? 'neutral',
    },
    {
      title: t('dashboard.todayRevenue'),
      value: stats ? formatNZD(stats.today_revenue?.value ?? 0, { maximumFractionDigits: 0 }) : formatNZD(0, { maximumFractionDigits: 0 }),
      change: stats
        ? {
            value: Number(stats.today_revenue?.change ?? 0),
            label: t('dashboard.comparedToYesterday'),
          }
        : undefined,
      icon: DollarSign,
      color: 'success' as const,
      trend: stats?.today_revenue?.trend ?? 'neutral',
      sensitive: true,
    },
    {
      title: t('dashboard.activeUsers'),
      value: stats ? stats.active_users?.value ?? 0 : 0,
      change: stats
        ? {
            value: Number(stats.active_users?.change ?? 0),
            label: t('dashboard.comparedToLastMonth'),
          }
        : undefined,
      icon: Users,
      color: 'warning' as const,
      trend: stats?.active_users?.trend ?? 'neutral',
    },
    {
      title: t('dashboard.courtUtilization'),
      value: `${stats?.avg_utilization?.value ?? 0}%`,
      change: stats
        ? {
            value: Number(stats.avg_utilization?.change ?? 0),
            label: t('dashboard.comparedToLastWeek'),
          }
        : undefined,
      icon: TrendingUp,
      color: 'red' as const,
      trend: stats?.avg_utilization?.trend ?? 'neutral',
    },
  ];

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('dashboard.title')}</h1>
        <p className="text-gray-500">
          {t('dashboard.todayIs')}{' '}
          {new Date().toLocaleDateString(i18n.language === 'zh' ? 'zh-CN' : 'en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            weekday: 'long',
            timeZone: 'Pacific/Auckland'
          })}
        </p>
      </motion.div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        {statsData.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.1 }}
          >
            <StatsCard {...stat} loading={false} />
          </motion.div>
        ))}
      </div>

      {/* 主要内容区域 */}
      <div>
        <RevenueChart data={revenueData} loading={revenueLoading} />
      </div>

      {/* 快速操作 */}
      <QuickActions />

      {/* 今日预订列表 */}
      <TodayBookings bookings={todayBookings} loading={todayBookingsLoading} />
    </div>
  );
};

export default Dashboard;
